import { useMemo, useCallback } from 'react';
import { Trash2, ShoppingCart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useFrappeFileUrl } from '@/lib/utils/frappeUrl';
import { formatCurrency } from './formatCurrency';
import type { POSCartItem } from './types';

interface POSCartSidebarProps {
  cartItems: POSCartItem[];
  currency: string;
  onRemoveItem?: (itemCode: string, lineId?: string) => void;
  onClose: () => void;
}

/**
 * Cart Sidebar component - shows added items with summary
 */
export function POSCartSidebar({ cartItems, currency, onRemoveItem, onClose }: POSCartSidebarProps) {
  const toAbsolute = useFrappeFileUrl();

  const totalQty = useMemo(
    () => cartItems.reduce((sum, item) => sum + (item.qty || 0), 0),
    [cartItems]
  );

  const totalAmount = useMemo(
    () => cartItems.reduce((sum, item) => sum + (item.amount || 0), 0),
    [cartItems]
  );

  const handleClearCart = useCallback(() => {
    if (!onRemoveItem) return;
    cartItems.forEach((item) => {
      if (item.item_code) {
        onRemoveItem(item.item_code, item.line_id);
      }
    });
  }, [cartItems, onRemoveItem]);

  return (
    <div className="w-96 border-l bg-muted/30 flex flex-col h-full">
      {/* Cart Header */}
      <div className="px-4 py-4 border-b bg-background">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded bg-[#007AFF]/10 flex items-center justify-center flex-shrink-0">
              <ShoppingCart className="w-5 h-5 text-[#007AFF]" />
            </div>
            <div className="flex flex-col space-y-0.5">
              {cartItems.length > 0 ? (
                <>
                  <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
                    Total Items
                  </span>
                  <span className="text-base font-medium text-foreground leading-tight">
                    {cartItems.length} {cartItems.length === 1 ? 'item' : 'items'}
                  </span>
                  <span className="text-xs text-muted-foreground font-normal">
                    {totalQty} {totalQty === 1 ? 'unit' : 'units'} ·{' '}
                    {formatCurrency(totalAmount, currency)}
                  </span>
                </>
              ) : (
                <>
                  <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
                    Cart
                  </span>
                  <span className="text-base font-medium text-foreground leading-tight">
                    Empty
                  </span>
                  <span className="text-xs text-muted-foreground font-normal">
                    Click products to add
                  </span>
                </>
              )}
            </div>
          </div>

          {cartItems.length > 0 && onRemoveItem && (
            <button
              onClick={handleClearCart}
              className="p-2 hover:bg-destructive/10 rounded-sm text-muted-foreground hover:text-destructive transition-colors"
              title="Clear cart"
              aria-label="Clear cart"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Cart Items */}
      <div className="flex-1 overflow-auto">
        {cartItems.length === 0 ? (
          <div className="p-6 text-center text-muted-foreground text-sm">
            <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No items in cart</p>
            <p className="text-xs mt-1">Click on products to add them</p>
          </div>
        ) : (
          <div className="p-3 space-y-2">
            {cartItems.map((item) => {
              const imageUrl = item.image ? toAbsolute(item.image) : null;
              return (
                <div key={item.line_id || item.item_code} className="bg-background rounded-sm p-3 border">
                  <div className="flex items-start gap-3">
                    <div className="w-14 h-14 rounded-xs overflow-hidden bg-muted flex-shrink-0 flex items-center justify-center">
                      {imageUrl ? (
                        <img src={imageUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-muted-foreground text-lg font-medium">
                          {(item.item_name || item.item_code || '?')[0].toUpperCase()}
                        </span>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">
                            {item.item_name || item.item_code}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">
                            {item.item_code}
                          </div>
                          {item.note && (
                            <div className="mt-0.5 text-xs text-foreground/80 line-clamp-2">
                              {item.note}
                            </div>
                          )}
                        </div>
                        {onRemoveItem && (
                          <button
                            onClick={() => onRemoveItem(item.item_code || '', item.line_id)}
                            className="p-1 hover:bg-destructive/10 rounded-md text-muted-foreground hover:text-destructive transition-colors flex-shrink-0"
                            title="Remove item"
                            aria-label="Remove item"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <div className="mt-1.5 flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">
                          {item.qty || 1} × {formatCurrency(item.rate, currency)}
                        </span>
                        <span className="font-medium">{formatCurrency(item.amount, currency)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Cart Summary */}
      <div className="border-t bg-background p-4 space-y-3">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Items</span>
          <span>{cartItems.length}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Total Qty</span>
          <span>{totalQty}</span>
        </div>
        <div className="flex items-center justify-between font-semibold">
          <span>Total</span>
          <span className="text-lg">{formatCurrency(totalAmount, currency)}</span>
        </div>
        <Button
          className="w-full bg-[#007AFF] hover:bg-[#007AFF]/90 text-white"
          size="lg"
          onClick={onClose}
        >
          Continue
        </Button>
      </div>
    </div>
  );
}
